import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { buildCompanyScoresByDay } from "../../hooks/kpi";
import { metaByIdFromDefs } from "../../helpers/scoreAggregate";
import { periodEndFor } from "../../helpers/workingDays";
import { companyToday } from "../../helpers/companyTime";
import { lookAheadEnd } from "./lookAhead";
import { fetchWorkspaceScores, monthsForWindow, resolveWindow, sumScores } from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";

/**
 * Total Revenue is a special case: Kelvin's ask was "make sure it gets the
 * SAME Total Revenue data as Payroll vs Revenue" (components/LaborMetrics).
 * Going through the generic metric picker (useScopedMetrics, name-matched)
 * risked reading a same-named-but-different per-workspace KPI, or simply
 * disagreeing with Labor's own aggregation on a rounding edge — a real KPI
 * type flag exists for exactly this ("model.type === total_revenue", the
 * ONE metric that flag ever means — see LaborSetup's own totalRevenueKpi()),
 * so this reads it through the IDENTICAL fetch (fetchWorkspaceScores) and
 * fold (sumScores) Labor already uses, rather than trusting two pipelines to
 * agree. The two cards can now never quietly disagree.
 */

/** The company's one canonical KPI of a given rollup `type` (same "exactly
 *  one real source" rule as LaborSetup.jsx's totalRevenueKpi()), or null. */
export const findRollupKpi = (companyKpis, workspaces, type) => {
  for (let i = 0; i < (workspaces || []).length; i += 1) {
    const w = workspaces[i];
    const hit = (companyKpis?.[String(w?._id)] || []).find(
      (k) => k && !k.isDraft && !k.disabled && (k.type === type || k?.model?.type === type),
    );
    if (hit) return { workspaceId: String(w._id), kpiId: String(hit._id) };
  }
  return null;
};

/** The company's one canonical Total Revenue KPI, or null. */
export const findTotalRevenueKpi = (companyKpis, workspaces) => findRollupKpi(companyKpis, workspaces, "total_revenue");

/** The company's one canonical Total Scheduled Revenue KPI, or null — the
 *  same rollup-model story as Total Revenue (see findTotalRevenueKpi), just
 *  scoped to KpiModel type "scheduled_revenue" instead. */
export const findScheduledRevenueKpi = (companyKpis, workspaces) => findRollupKpi(companyKpis, workspaces, "scheduled_revenue");

/** True when the picked metric IS (or resolves to) the given rollup KPI — by
 *  real id when the name-matched metric gave us one, by title (the Analytics
 *  picker's id is the metric's title, see data.js), or by the resolved
 *  metric's own model type once it's loaded. */
export const isRollupPick = (metricId, rollupKpi, metric, title, type) => {
  if (!metricId) return false;
  if (rollupKpi && String(metricId) === rollupKpi.kpiId) return true;
  if (String(metricId).trim().toLowerCase() === title) return true;
  const mk = (metric?.title || metric?.name || "").trim().toLowerCase();
  if (mk && mk === title) return true;
  const mt = metric?.type || metric?.model?.type;
  return !!type && mt === type;
};

/** True when the picked metric IS (or resolves to) the canonical Total
 *  Revenue KPI. */
export const isTotalRevenuePick = (metricId, revKpi, metric) => (
  isRollupPick(metricId, revKpi, metric, "total revenue", "total_revenue")
);

/** Every YYYY-MM from `start`'s month to `end`'s month. */
const monthsSpanning = (start, end) => {
  const out = [];
  const cursor = start.clone().startOf("month");
  let guard = 0;
  while (cursor.isSameOrBefore(end, "month") && guard < 36) {
    out.push(cursor.format("YYYY-MM"));
    cursor.add(1, "month");
    guard += 1;
  }
  return out;
};

/**
 * The window, run out to the period's end when asked, and on past it by
 * `aheadDays` for the look-ahead. `to` is always the PERIOD's end (what the
 * total and the goal cover); `chartTo` is how far the daily series reaches.
 */
const windowFor = (win, custom, throughPeriodEnd, aheadDays = 0) => {
  const { from, to } = resolveWindow(win, custom);
  if (!throughPeriodEnd || !from || !to || !from.isValid() || !to.isValid()) return { from, to, chartTo: to };
  const periodTo = periodEndFor(win, to, custom);
  return { from, to: periodTo, chartTo: lookAheadEnd(periodTo, aheadDays, companyToday()) };
};

/**
 * @param {{workspaceId:string, kpiId:string}|null} revKpi
 * @param {string} bucket  today|week|month|lastMonth|quarter|ytd|custom (resolveWindow ids)
 * @param {{from,to}|null} custom
 * @param {boolean} throughPeriodEnd  read to the END of the period instead of
 *   stopping at today. Total Scheduled Revenue only: work booked for the days
 *   still to come is exactly what it exists to show, and the window's usual
 *   "nothing scores after today" cap would drop it from the total and the bars.
 * @param {number} aheadDays  extra days past the period's end for the daily
 *   series only (see lookAhead.js). They are NOT in `amount`; `aheadAmount`
 *   is what they add up to.
 */
export default function useTotalRevenue(revKpi, bucket, custom, throughPeriodEnd = false, aheadDays = 0) {
  const companyId = useSelector((s) => s.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const wsId = revKpi?.workspaceId || null;
  const kpiId = revKpi?.kpiId || null;

  const win = bucket === "year" ? "ytd" : (bucket || "month");
  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  const months = useMemo(() => {
    if (!throughPeriodEnd) return monthsForWindow(win, custom);
    const { from, chartTo } = windowFor(win, custom, true, aheadDays);
    return from && chartTo && from.isValid() && chartTo.isValid() ? monthsSpanning(from, chartTo) : monthsForWindow(win, custom);
  }, [win, customKey, throughPeriodEnd, aheadDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const [scores, setScores] = useState(null);
  const fetchKey = `${companyId || ""}|${wsId || ""}|${months.join(",")}`;
  useEffect(() => {
    if (!companyId || !wsId) { setScores(null); return undefined; }
    let alive = true;
    fetchWorkspaceScores(companyId, wsId, months)
      .then((s) => { if (alive) setScores(s || []); })
      .catch(() => { if (alive) setScores([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, fetchKey]);

  return useMemo(() => {
    if (!wsId || !kpiId) return { active: false, amount: 0, aheadAmount: 0, dailySeries: [], loading: false };
    if (scores == null) return { active: true, amount: 0, aheadAmount: 0, dailySeries: [], loading: true };

    const { from, to, chartTo } = windowFor(win, custom, throughPeriodEnd, aheadDays);
    const defs = (companyKpis && companyKpis[wsId]) || [];
    const { perKpi } = sumScores(scores, from, metaByIdFromDefs(defs), to);
    const amount = Number(perKpi?.[kpiId]?.amt) || 0;

    const dailySeries = [];
    if (from && chartTo && from.isValid() && chartTo.isValid()) {
      const byDay = {};
      const cursor = from.clone().startOf("month");
      const lastMonth = chartTo.clone().startOf("month");
      let guard = 0;
      while (cursor.isSameOrBefore(lastMonth, "month") && guard < 36) {
        Object.assign(byDay, buildCompanyScoresByDay(scores, cursor));
        cursor.add(1, "month");
        guard += 1;
      }
      const day = from.clone().startOf("day");
      const end = chartTo.clone().startOf("day");
      let dayGuard = 0;
      while (day.isSameOrBefore(end, "day") && dayGuard < 400) {
        const key = day.format("YYYY-MM-DD");
        const cell = byDay[key]?.[kpiId];
        dailySeries.push({ date: key, value: typeof cell?.amt === "number" ? cell.amt : 0 });
        day.add(1, "day");
        dayGuard += 1;
      }
    }

    /* The days past the period's end, on their own: shown under the chart but
       never added to the total or measured against the goal. */
    const periodLast = to && to.isValid() ? to.format("YYYY-MM-DD") : null;
    const aheadAmount = periodLast
      ? dailySeries.filter((d) => d.date > periodLast).reduce((sum, d) => sum + d.value, 0)
      : 0;

    return { active: true, amount, aheadAmount, dailySeries, loading: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores, wsId, kpiId, companyKpis, win, customKey, throughPeriodEnd, aheadDays]);
}
